import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster, toast } from "sonner";

// Envia cookies em TODAS as chamadas do axios (necessário para o cookie de auth httpOnly)
axios.defaults.withCredentials = true;
import { SisMacomSidebar } from "./components/SisMacomSidebar";
import { SisMacomTopNav } from "./components/SisMacomTopNav";
import { MasonicDashboard } from "./components/MasonicDashboard";
import { MembersDirectory } from "./components/MembersDirectory";
import { AttendanceSessions } from "./components/AttendanceSessions";
import { InstructionGroups } from "./components/InstructionGroups";
import { LodgeFinance } from "./components/LodgeFinance";
import { CostCenterReport } from "./components/CostCenterReport";
import { LodgeProfileView } from "./components/LodgeProfileView";
import { LodgeAgendaView } from "./components/LodgeAgendaView";
import { LodgeMessagesView } from "./components/LodgeMessagesView";
import { LodgeFilesView } from "./components/LodgeFilesView";
import { FinancialReportView } from "./components/FinancialReportView";
import { MemberModal } from "./components/MemberModal";
import { SessionModal } from "./components/SessionModal";
import { GroupModal } from "./components/GroupModal";
import { FinanceModal } from "./components/FinanceModal";
import { CostCenterModal } from "./components/CostCenterModal";
import { BankAccountModal } from "./components/BankAccountModal";
import { TransferModal } from "./components/TransferModal";
import { ClassifyModal } from "./components/ClassifyModal";
import { ImportStatementModal } from "./components/ImportStatementModal";
import { ChangeBankModal } from "./components/ChangeBankModal";
import { FilantropiaPublicPage } from "./components/FilantropiaPublicPage";
import { ChangePasswordModal } from "./components/ChangePasswordModal";
import { MemberStatementModal } from "./components/MemberStatementModal";
import { LoginModal } from "./components/LoginModal";
import { SessionQRModal } from "./components/SessionQRModal";
import { SendBoletimModal } from "./components/SendBoletimModal";
import { CheckinPublicPage } from "./components/CheckinPublicPage";
import { CandidatesView } from "./components/CandidatesView";
import { BalaustresView } from "./components/BalaustresView";
import { UsersView } from "./components/UsersView";
import { AuditLogsView } from "./components/AuditLogsView";
import { BalaustreTemplatesView } from "./components/BalaustreTemplatesView";
import { LojaSignupPage } from "./components/LojaSignupPage";
import { LojaSettingsView } from "./components/LojaSettingsView";
import { DiretoriaGestaoView } from "./components/DiretoriaGestaoView";
import { LojaPublicPage } from "./components/LojaPublicPage";
import { PricingPage } from "./components/PricingPage";
import { PaymentSuccessPage } from "./components/PaymentSuccessPage";
import { GrandOrientDashboard } from "./components/GrandOrientDashboard";
import { ContactsView } from "./components/ContactsView";
import { ReceiptPrintModal } from "./components/ReceiptPrintModal";
import { CommemorativeDatesView } from "./components/CommemorativeDatesView";
import { AttendanceQRView } from "./components/AttendanceQRView";
import { AttendanceReportView } from "./components/AttendanceReportView";
import { FeaturesPage } from "./components/FeaturesPage";
import { BlogListPage } from "./components/BlogListPage";
import { BlogPostPage } from "./components/BlogPostPage";
import { PublicHome } from "./components/PublicHome";
import { SuperAdminPanel } from "./components/SuperAdminPanel";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import { formatLojaName } from "./utils/lodgeName";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
let homeAuthCheckPromise;

const checkHomeAuthentication = () => {
  if (!homeAuthCheckPromise) {
    homeAuthCheckPromise = axios.get(`${API}/auth/me`, { withCredentials: true });
  }
  return homeAuthCheckPromise;
};

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/checkin" element={<CheckinPublicPage />} />
        <Route path="/signup" element={<LojaSignupPage />} />
        <Route path="/loja/:lojaNumber" element={<LojaPublicPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/admin" element={<SuperAdminPanel />} />
        <Route path="/public/filantropia/:token" element={<FilantropiaPublicPage />} />
        <Route path="/painel/*" element={<MainApp />} />
        <Route path="/" element={<HomeGate />} />
        <Route path="*" element={<MainApp />} />
      </Routes>
    </BrowserRouter>
  );
}

/** Roteador raiz: mostra PublicHome pra visitante anônimo; MainApp para Loja logada; /admin pro super-admin. */
function HomeGate() {
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await checkHomeAuthentication();
        if (cancelled) return;
        if (res.data?.is_super_admin) {
          window.location.replace("/admin");
          return;
        }
        setAuthed(true);
      } catch {
        setAuthed(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-[#080C14] flex items-center justify-center">
        <div className="text-[#D4AF37] text-xs uppercase tracking-widest animate-pulse">Carregando…</div>
      </div>
    );
  }
  return authed ? <MainApp /> : <PublicHome />;
}

function MainApp() {
  const [activeModule, setActiveModule] = useState("dashboard");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Auth User state — hidratado via /api/auth/me no mount
  const [currentUser, setCurrentUser] = useState(null);

  // Permissão RBAC: Secretaria ou Master da Loja pode gerenciar membros
  const canManageMembers = useMemo(() => {
    const roles = Array.isArray(currentUser?.roles)
      ? currentUser.roles.map((r) => String(r).toLowerCase())
      : [];
    return roles.includes("secretaria") || roles.includes("master da loja") || roles.includes("webmaster");
  }, [currentUser]);

  // Permissão Agenda: Venerável Mestre, Secretário, Master ou WebMaster podem criar sessões
  const canScheduleSession = useMemo(() => {
    const roles = Array.isArray(currentUser?.roles)
      ? currentUser.roles.map((r) => String(r).toLowerCase())
      : [];
    const office = String(currentUser?.office || "").toLowerCase();
    return (
      roles.includes("secretaria") ||
      roles.includes("master da loja") ||
      roles.includes("webmaster") ||
      office.includes("venerável") ||
      office.includes("veneravel") ||
      office.includes("secretário") ||
      office.includes("secretario")
    );
  }, [currentUser]);

  // Permissão Tesouraria: somente Tesoureiro/Master pode dar baixa em mensalidades
  const canManageTreasury = useMemo(() => {
    const roles = Array.isArray(currentUser?.roles)
      ? currentUser.roles.map((r) => String(r).toLowerCase())
      : [];
    return (
      roles.includes("tesouraria") ||
      roles.includes("master da loja") ||
      roles.includes("webmaster") ||
      currentUser?.is_master_loja === true ||
      currentUser?.is_super_admin === true
    );
  }, [currentUser]);

  // Landing page adaptativa: Irmão comum entra em "Perfil da Loja" (não no painel Master)
  useEffect(() => {
    if (currentUser && !canManageMembers && activeModule === "dashboard") {
      setActiveModule("perfil_loja");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, canManageMembers]);

  // Core Data
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [treasurySettings, setTreasurySettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Receipt print modal state
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [activeReceipt, setActiveReceipt] = useState(null);

  // Modals
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  const [isFinanceModalOpen, setIsFinanceModalOpen] = useState(false);
  const [isCostCenterModalOpen, setIsCostCenterModalOpen] = useState(false);
  const [editingCostCenter, setEditingCostCenter] = useState(null);
  const [isBankAccountModalOpen, setIsBankAccountModalOpen] = useState(false);
  const [editingBankAccount, setEditingBankAccount] = useState(null);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [classifyingTx, setClassifyingTx] = useState(null);
  const [editingBankTx, setEditingBankTx] = useState(null);
  const [isImportStatementOpen, setIsImportStatementOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const [isStatementModalOpen, setIsStatementModalOpen] = useState(false);
  const [selectedStatementData, setSelectedStatementData] = useState(null);

  // QR Code & Boletim Modals
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [selectedSessionForQR, setSelectedSessionForQR] = useState(null);
  const [isBoletimModalOpen, setIsBoletimModalOpen] = useState(false);
  const [selectedSessionForBoletim, setSelectedSessionForBoletim] = useState(null);
  const [boletimRefreshKey, setBoletimRefreshKey] = useState(0);

  // Fetch all initial data
  const fetchData = async () => {
    try {
      const [statsRes, membersRes, sessionsRes, groupsRes, txsRes, ccsRes, settingsRes, contactsRes, banksRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/members`),
        axios.get(`${API}/sessions`),
        axios.get(`${API}/groups`),
        axios.get(`${API}/finance`),
        axios.get(`${API}/treasury/cost-centers`),
        axios.get(`${API}/treasury/settings`),
        axios.get(`${API}/contacts`).catch(() => ({ data: [] })),
        axios.get(`${API}/treasury/bank-accounts`).catch(() => ({ data: [] })),
      ]);

      setStats(statsRes.data);
      setMembers(membersRes.data);
      setSessions(sessionsRes.data);
      setGroups(groupsRes.data);
      setTransactions(txsRes.data);
      setCostCenters(ccsRes.data);
      setTreasurySettings(settingsRes.data);
      setContacts(contactsRes.data || []);
      setBankAccounts(banksRes.data || []);
    } catch (err) {
      console.error("Erro ao carregar dados da Loja:", err);
      toast.error("Erro ao conectar com a API da Loja Maçônica.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    const bootstrapAuthenticatedApp = async () => {
      try {
        const authResponse = await axios.get(`${API}/auth/me`);
        if (!active) return;
        if (authResponse.data?.is_super_admin) {
          window.location.replace("/admin");
          return;
        }
        setCurrentUser(authResponse.data);
        const lojaResponse = await axios.get(`${API}/lojas/current`);
        if (active) setCurrentLoja(lojaResponse.data);
        await fetchData();
      } catch {
        if (!active) return;
        setCurrentUser(null);
        setLoading(false);
        setIsLoginModalOpen(true);
      }
    };
    bootstrapAuthenticatedApp();
    return () => { active = false; };
  }, []);

  const [currentLoja, setCurrentLoja] = useState(null);

  // --- Auth Handlers ---
  const handleLogin = async (email, password, lojaId) => {
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password, loja_id: lojaId });
      // Se for super-admin da plataforma, sempre redireciona para o Gerenciador do Sistema
      if (res.data?.user?.is_super_admin) {
        toast.success(`Bem-vindo, ${res.data.user.name}! Redirecionando ao Gerenciador…`);
        setIsLoginModalOpen(false);
        window.location.replace("/admin");
        return;
      }
      setCurrentUser(res.data.user);
      if (res.data.loja) setCurrentLoja(res.data.loja);
      toast.success(`Bem-vindo, ${res.data.user.name}! Loja: ${res.data.loja?.name || ""}`);
      setIsLoginModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro de login.");
      throw err;
    }
  };

  const handleResetData = async () => {
    if (!window.confirm("Deseja restaurar todos os dados litúrgicos e financeiros para o padrão inicial?")) return;
    try {
      await axios.post(`${API}/seed`);
      toast.success("Dados da Loja restaurados com sucesso!");
      fetchData();
    } catch (e) {
      toast.error("Erro ao restaurar dados.");
    }
  };

  // Statement & Capitation Handlers
  const handleViewMemberStatement = async (memberId) => {
    try {
      const res = await axios.get(`${API}/members/${memberId}/statement`);
      setSelectedStatementData(res.data);
      setIsStatementModalOpen(true);
    } catch (err) {
      toast.error("Erro ao carregar extrato do Irmão.");
    }
  };

  const handlePayFee = async (payPayload) => {
    try {
      const res = await axios.post(`${API}/treasury/pay-fee`, payPayload);
      toast.success(res.data.message);
      // Refresh current statement if open
      if (selectedStatementData?.member?.id) {
        const updated = await axios.get(`${API}/members/${selectedStatementData.member.id}/statement`);
        setSelectedStatementData(updated.data);
      }
      fetchData();
    } catch (err) {
      toast.error("Erro ao registrar pagamento.");
      throw err;
    }
  };

  // Cost Center Handlers
  const handleSaveCostCenter = async (payload) => {
    try {
      if (payload.id) {
        // Modo edição
        await axios.patch(`${API}/treasury/cost-centers/${payload.id}`, {
          code: payload.code,
          name: payload.name,
          description: payload.description,
          monthly_budget: payload.monthly_budget,
        });
        toast.success("Centro de custo atualizado!");
      } else {
        await axios.post(`${API}/treasury/cost-centers`, payload);
        toast.success("Centro de custo cadastrado com sucesso!");
      }
      setIsCostCenterModalOpen(false);
      setEditingCostCenter(null);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar centro de custo.");
    }
  };

  const handleEditCostCenter = (cc) => {
    setEditingCostCenter(cc);
    setIsCostCenterModalOpen(true);
  };

  const handleDeleteCostCenter = async (ccId) => {
    if (!window.confirm("Excluir este centro de custo?")) return;
    try {
      await axios.delete(`${API}/treasury/cost-centers/${ccId}`);
      toast.success("Centro de custo excluído com sucesso.");
      fetchData();
    } catch (err) {
      toast.error("Erro ao excluir centro de custo.");
    }
  };

  // Bank Account Handlers
  const handleSaveBankAccount = async (payload) => {
    try {
      if (payload.id) {
        await axios.patch(`${API}/treasury/bank-accounts/${payload.id}`, {
          name: payload.name,
          bank_name: payload.bank_name,
          branch: payload.branch,
          account_number: payload.account_number,
          account_kind: payload.account_kind,
          initial_balance: payload.initial_balance,
          color: payload.color,
          is_active: payload.is_active,
        });
        toast.success("Conta bancária atualizada!");
      } else {
        await axios.post(`${API}/treasury/bank-accounts`, {
          name: payload.name,
          bank_name: payload.bank_name,
          branch: payload.branch,
          account_number: payload.account_number,
          account_kind: payload.account_kind,
          initial_balance: payload.initial_balance,
          color: payload.color,
          is_active: payload.is_active,
        });
        toast.success("Conta bancária cadastrada!");
      }
      setIsBankAccountModalOpen(false);
      setEditingBankAccount(null);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar conta bancária.");
    }
  };

  const handleEditBankAccount = (ba) => {
    setEditingBankAccount(ba);
    setIsBankAccountModalOpen(true);
  };

  const handleDeleteBankAccount = async (baId) => {
    if (!window.confirm("Excluir esta conta bancária?\n\n⚠️ Só é possível excluir se não houver lançamentos vinculados.")) return;
    try {
      await axios.delete(`${API}/treasury/bank-accounts/${baId}`);
      toast.success("Conta bancária excluída.");
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao excluir conta.");
    }
  };

  // Transferências entre contas
  const handleTransfer = async (payload) => {
    try {
      const r = await axios.post(`${API}/treasury/transfer`, payload);
      toast.success(`Transferência de R$ ${payload.amount.toFixed(2)} efetuada!`);
      setIsTransferModalOpen(false);
      fetchData();
      return r.data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao transferir.");
    }
  };

  const handleCancelPendingTx = async (tx) => {
    if (!window.confirm(`Cancelar (excluir) a parcela "${tx.description}"?\n\nEste lançamento ainda está PENDENTE (não foi pago). Ao cancelar, ele será removido do sistema.\n\n⚠️ Se preferir estornar (mantendo histórico), pague primeiro e depois use "Estornar".`)) return;
    try {
      await axios.delete(`${API}/finance/${tx.id}`);
      toast.success("Parcela pendente cancelada.");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cancelar.");
    }
  };

  // Classificar lançamento pendente
  const handleClassify = async (payload) => {
    if (!classifyingTx) return;
    try {
      await axios.patch(`${API}/finance/${classifyingTx.id}/classify`, payload);
      toast.success("Lançamento classificado com sucesso!");
      setClassifyingTx(null);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao classificar lançamento.");
    }
  };

  // Treasury Settings Update
  const handleUpdateTreasurySettings = async (settingsPayload) => {
    try {
      await axios.post(`${API}/treasury/settings`, settingsPayload);
      toast.success("Regras de pontualidade atualizadas!");
      fetchData();
    } catch (err) {
      toast.error("Erro ao atualizar regras da tesouraria.");
    }
  };

  // Member Handlers
  const handleSaveMember = async (payload) => {
    try {
      if (editingMember) {
        await axios.put(`${API}/members/${editingMember.id}`, payload);
        toast.success("Dados do Irmão atualizados com sucesso!");
      } else {
        await axios.post(`${API}/members`, payload);
        toast.success("Irmão cadastrado no Quadro da Loja com sucesso!");
      }
      setIsMemberModalOpen(false);
      setEditingMember(null);
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro ao salvar Irmão.";
      toast.error(msg);
    }
  };

  const handleDeleteMember = async (member) => {
    if (!window.confirm(`Tem certeza que deseja excluir o Irmão ${member.name} do Quadro?`)) return;
    try {
      await axios.delete(`${API}/members/${member.id}`);
      toast.success("Irmão excluído com sucesso.");
      fetchData();
    } catch (e) {
      toast.error("Erro ao excluir Irmão.");
    }
  };

  // Session Handlers
  const handleSaveSession = async (payload) => {
    try {
      await axios.post(`${API}/sessions`, payload);
      toast.success("Sessão litúrgica cadastrada com sucesso!");
      setIsSessionModalOpen(false);
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro ao cadastrar sessão.";
      toast.error(msg);
    }
  };

  const handleUpdateAttendance = async (sessionId, presentIds, justifiedIds) => {
    await axios.post(`${API}/sessions/${sessionId}/attendance`, {
      present_member_ids: presentIds,
      justified_member_ids: justifiedIds
    });
    fetchData();
  };

  const handleLockAttendance = async (sessionId) => {
    try {
      const res = await axios.post(`${API}/sessions/${sessionId}/attendance/lock`);
      toast.success(res.data?.message || "Livro trancado com sucesso.");
      await fetchData();
      // devolve sessão atualizada para o child
      const updated = await axios.get(`${API}/sessions`);
      return (updated.data || []).find((s) => s.id === sessionId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao trancar o Livro.");
      return null;
    }
  };

  const handleUnlockAttendance = async (sessionId, justification) => {
    try {
      const res = await axios.post(`${API}/sessions/${sessionId}/attendance/unlock`, { justification });
      toast.success(res.data?.message || "Livro destravado.");
      await fetchData();
      const updated = await axios.get(`${API}/sessions`);
      return (updated.data || []).find((s) => s.id === sessionId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao destravar o Livro.");
      return null;
    }
  };

  const handleDeleteSession = async (session) => {
    if (!window.confirm(`Excluir a ata da Sessão nº ${session.session_number}?`)) return;
    try {
      await axios.delete(`${API}/sessions/${session.id}`);
      toast.success("Sessão excluída com sucesso.");
      fetchData();
    } catch (e) {
      toast.error("Erro ao excluir sessão.");
    }
  };

  const handleOpenQR = (session) => {
    setSelectedSessionForQR(session);
    setIsQRModalOpen(true);
  };

  const handleOpenBoletim = (session) => {
    setSelectedSessionForBoletim(session);
    setIsBoletimModalOpen(true);
  };

  // Group Handlers
  const handleSaveGroup = async (payload) => {
    try {
      if (editingGroup) {
        await axios.put(`${API}/groups/${editingGroup.id}`, payload);
        toast.success("Grupo de instrução atualizado!");
      } else {
        await axios.post(`${API}/groups`, payload);
        toast.success("Câmara de instrução / Comissão criada com sucesso!");
      }
      setIsGroupModalOpen(false);
      setEditingGroup(null);
      fetchData();
    } catch (err) {
      toast.error("Erro ao salvar grupo.");
    }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Excluir o grupo ${group.name}?`)) return;
    try {
      await axios.delete(`${API}/groups/${group.id}`);
      toast.success("Grupo excluído com sucesso.");
      fetchData();
    } catch (e) {
      toast.error("Erro ao excluir grupo.");
    }
  };

  // Finance Handlers
  const handleSaveFinance = async (payload) => {
    try {
      const res = await axios.post(`${API}/finance`, payload);
      toast.success("Lançamento financeiro realizado com sucesso!");
      if (res.data?.receipt_number && res.data?.email_sent_to) {
        toast.success(`Recibo ${res.data.receipt_number} enviado para ${res.data.email_sent_to}`);
      }
      setIsFinanceModalOpen(false);
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro ao efetuar lançamento financeiro.";
      toast.error(msg);
    }
  };

  const handleDeleteFinance = async (tx) => {
    if (!window.confirm("Excluir este lançamento financeiro?")) return;
    try {
      await axios.delete(`${API}/finance/${tx.id}`);
      toast.success("Lançamento excluído com sucesso.");
      fetchData();
    } catch (e) {
      toast.error("Erro ao excluir lançamento.");
    }
  };

  const handleReverseFinance = async (tx) => {
    const desc = (tx.description || "").substring(0, 50);
    const reason = window.prompt(
      `Estornar este lançamento?\n\n"${desc}"\nValor: R$ ${(tx.amount || 0).toLocaleString("pt-BR")}\n\nDigite o motivo do estorno (aparece na descrição da linha de compensação):`,
      "Correção de lançamento"
    );
    if (reason === null) return;
    try {
      await axios.post(`${API}/finance/${tx.id}/reverse`, { reason });
      toast.success("Lançamento estornado. Uma linha de compensação foi criada.");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao estornar lançamento.");
    }
  };

  const handleCancelBatch = async (tx) => {
    if (!tx.batch_id) return;
    const confirmReverse = window.confirm(
      "Cancelar este cronograma completo?\n\n" +
      "• Parcelas PENDENTES serão canceladas\n" +
      "• Parcelas JÁ PAGAS serão ESTORNADAS automaticamente (dinheiro volta ao caixa, mensalidades vinculadas reabrem)\n\n" +
      "Clique OK para continuar ou Cancelar para abortar."
    );
    if (!confirmReverse) return;
    const reason = window.prompt(
      "Motivo do cancelamento (ficará no histórico de auditoria):",
      "Contrato encerrado / Recorrência descontinuada"
    );
    if (reason === null) return;
    try {
      const r = await axios.post(`${API}/finance/installments/batch/${tx.batch_id}/cancel`, { reason, reverse_paid: true });
      const d = r.data || {};
      let msg = `${d.cancelled || 0} parcela(s) pendente(s) canceladas`;
      if (d.reversed) msg += ` • ${d.reversed} parcela(s) paga(s) estornadas`;
      if (d.fees_reverted && d.fees_reverted.length) msg += ` • ${d.fees_reverted.length} mensalidade(s) reaberta(s)`;
      toast.success(msg + ".");
      if (d.reverse_errors && d.reverse_errors.length) {
        toast.warning(`${d.reverse_errors.length} lançamento(s) não puderam ser estornados (ver auditoria).`);
      }
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cancelar cronograma.");
    }
  };

  const handleViewReceipt = async (tx) => {
    try {
      const res = await axios.get(`${API}/finance/${tx.id}/receipt`);
      setActiveReceipt(res.data);
      setReceiptModalOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar recibo.");
    }
  };

  const handleMarkAsPaid = async (tx) => {
    if (!window.confirm(`Dar baixa em "${tx.description}"? Isso emitirá um recibo com numeração automática.`)) return false;
    try {
      const res = await axios.post(`${API}/finance/${tx.id}/mark-paid`);
      toast.success(`Recibo ${res.data.receipt?.number_label || ""} emitido!`);
      if (res.data.email?.success) {
        toast.success(`E-mail enviado para ${res.data.email.to}`);
      } else if (res.data.email?.reason) {
        toast.info(`Recibo emitido — envio de e-mail: ${res.data.email.reason}`);
      }
      await fetchData();
      setActiveReceipt(res.data.receipt);
      setReceiptModalOpen(true);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao marcar como pago.");
      return false;
    }
  };

  const handleSendReceiptEmail = async (receipt) => {
    if (!receipt?.transaction_id) return;
    try {
      const res = await axios.post(`${API}/finance/${receipt.transaction_id}/send-receipt-email`);
      if (res.data?.success) {
        toast.success(`Recibo enviado para ${res.data.to}`);
      } else if (res.data?.skipped) {
        toast.info(res.data.reason || "Envio ignorado.");
      } else {
        toast.error(res.data?.error || "Falha no envio.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar e-mail.");
    }
  };

  if (!currentUser) {
    return (
      <div data-testid="authenticated-app-gate" className="min-h-screen bg-[#080C14] text-white grid place-items-center px-4">
        <Toaster position="top-right" richColors theme="dark" />
        <div className="text-center">
          <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 font-serif text-2xl font-bold text-[#D4AF37]">LI</div>
          <h1 className="font-serif text-4xl font-bold">Lojas Integradas</h1>
          <p className="mt-2 text-sm text-slate-500">{loading ? "Verificando sua sessão…" : "Acesse para continuar."}</p>
          {!loading && !isLoginModalOpen && (
            <button data-testid="auth-gate-open-login" onClick={() => setIsLoginModalOpen(true)} className="mt-5 rounded-lg bg-[#D4AF37] px-5 py-2.5 text-xs font-bold uppercase text-slate-950">Entrar</button>
          )}
        </div>
        <LoginModal
          isOpen={!loading && isLoginModalOpen}
          onClose={() => setIsLoginModalOpen(false)}
          onLogin={handleLogin}
          currentUser={currentUser}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080C14] text-[#F8FAFC] flex flex-col selection:bg-[#D4AF37] selection:text-slate-950 font-sans">
      <Toaster position="top-right" richColors theme="dark" />
      <ImpersonationBanner />
      <div className="flex flex-1 min-h-0">

      {/* SisMaçom Collapsible Sidebar */}
      <div className={`${isSidebarOpen ? "block" : "hidden"} lg:block fixed lg:static z-40 inset-y-0 left-0`}>
        <SisMacomSidebar
          activeModule={activeModule}
          setActiveModule={(mod) => {
            setActiveModule(mod);
            setIsSidebarOpen(false);
          }}
          currentUser={currentUser}
          currentLoja={currentLoja}
          onOpenLoginModal={() => setIsLoginModalOpen(true)}
          onOpenMyStatement={(mId) => handleViewMemberStatement(mId || currentUser?.member_id || members[0]?.id)}
          onResetData={handleResetData}
        />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 min-h-screen overflow-x-hidden">
        
        {/* SisMaçom Top Nav */}
        <SisMacomTopNav
          activeModule={activeModule}
          currentUser={currentUser}
          currentLoja={currentLoja}
          onOpenLoginModal={() => setIsLoginModalOpen(true)}
          onOpenMyStatement={(mId) => handleViewMemberStatement(mId || currentUser?.member_id || members[0]?.id)}
          onOpenNewSessionModal={() => setIsSessionModalOpen(true)}
          onOpenNewMemberModal={() => {
            setEditingMember(null);
            setIsMemberModalOpen(true);
          }}
          onOpenNewFinanceModal={() => setIsFinanceModalOpen(true)}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          canManage={canManageMembers}
        />

        {/* Dynamic Views based on SisMaçom active module */}
        <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {/* Presidência / Painel Geral */}
              {activeModule === "dashboard" && (
                <MasonicDashboard
                  stats={stats}
                  members={members}
                  sessions={sessions}
                  groups={groups}
                  currentLoja={currentLoja}
                  onNavigateTab={setActiveModule}
                  onOpenNewMemberModal={() => {
                    setEditingMember(null);
                    setIsMemberModalOpen(true);
                  }}
                  onOpenNewSessionModal={() => setIsSessionModalOpen(true)}
                  onOpenNewGroupModal={() => {
                    setEditingGroup(null);
                    setIsGroupModalOpen(true);
                  }}
                  onOpenNewFinanceModal={() => setIsFinanceModalOpen(true)}
                  canManage={canManageMembers}
                />
              )}
              {activeModule === "obreiros" && (
                <MembersDirectory
                  members={members}
                  lodgeInfo={{ name: currentLoja?.name, number: currentLoja?.number, oriente: currentLoja?.oriente }}
                  onOpenNewMemberModal={() => {
                    setEditingMember(null);
                    setIsMemberModalOpen(true);
                  }}
                  onOpenEditMemberModal={(member) => {
                    setEditingMember(member);
                    setIsMemberModalOpen(true);
                  }}
                  onDeleteMember={handleDeleteMember}
                  onViewMemberStatement={handleViewMemberStatement}
                  canManage={canManageMembers}
                />
              )}

              {/* Chancelaria / Livro de Presença */}
              {activeModule === "frequencia" && (
                <AttendanceSessions
                  sessions={sessions}
                  members={members}
                  onOpenNewSessionModal={() => setIsSessionModalOpen(true)}
                  onOpenEditSessionModal={(session) => {
                    // edit session
                  }}
                  onUpdateAttendance={handleUpdateAttendance}
                  onLockAttendance={handleLockAttendance}
                  onUnlockAttendance={handleUnlockAttendance}
                  onDeleteSession={handleDeleteSession}
                  onOpenQR={handleOpenQR}
                  onOpenBoletim={handleOpenBoletim}
                  boletimRefreshKey={boletimRefreshKey}
                />
              )}

              {/* Chancelaria / Relatório de Frequência */}
              {activeModule === "relatorio_frequencia" && (
                <AttendanceReportView />
              )}

              {/* Relatórios / Balancete Consolidado */}
              {activeModule === "relatorios_balancete" && (
                <FinancialReportView costCenters={costCenters} />
              )}

              {/* Secretaria / Candidatos */}
              {activeModule === "candidatos" && (
                <CandidatesView members={members} />
              )}

              {/* Secretaria / Balaústres */}
              {activeModule === "balaustres" && (
                <BalaustresView sessions={sessions} members={members} currentLoja={currentLoja} />
              )}

              {/* Secretaria / Usuários */}
              {activeModule === "usuarios" && (
                <UsersView members={members} />
              )}

              {/* Presidência / Auditoria */}
              {activeModule === "auditoria" && (
                <AuditLogsView />
              )}

              {/* Presidência / Dashboard do Plano Ouro (Plano Grande Oriente) */}
              {activeModule === "grand_orient" && (
                <GrandOrientDashboard />
              )}

              {/* Chancelaria / Templates */}
              {activeModule === "templates_balaustre" && (
                <BalaustreTemplatesView />
              )}

              {/* Presidência / Configurações da Loja (Papel Timbrado) */}
              {activeModule === "loja_settings" && (
                <LojaSettingsView
                  currentLoja={currentLoja}
                  onUpdated={(loja) => setCurrentLoja(loja)}
                />
              )}

              {/* Diretoria da Gestão (eleição de cargos + transferência de Master) */}
              {activeModule === "diretoria_gestao" && (
                <DiretoriaGestaoView
                  members={members}
                  currentUser={currentUser}
                  currentLoja={currentLoja}
                  treasurySettings={treasurySettings}
                  onReload={async () => {
                    await fetchData();
                    axios.get(`${API}/auth/me`).then((r) => setCurrentUser(r.data)).catch(() => {});
                  }}
                />
              )}

              {/* Secretaria / Câmaras de Instrução & Comissões */}
              {activeModule === "instrucao" && (
                <InstructionGroups
                  groups={groups}
                  members={members}
                  onOpenNewGroupModal={() => {
                    setEditingGroup(null);
                    setIsGroupModalOpen(true);
                  }}
                  onOpenEditGroupModal={(group) => {
                    setEditingGroup(group);
                    setIsGroupModalOpen(true);
                  }}
                  onDeleteGroup={handleDeleteGroup}
                />
              )}

              {/* Tesouraria / Conta Corrente, Centros de Custo & Descontos */}
              {(activeModule === "tesouraria" || activeModule === "centros_custo" || activeModule === "mensalidades_desconto") && (
                <LodgeFinance
                  transactions={transactions}
                  members={members}
                  contacts={contacts}
                  stats={stats}
                  costCenters={costCenters}
                  bankAccounts={bankAccounts}
                  treasurySettings={treasurySettings}
                  initialSubTab={activeModule === "centros_custo" ? "centros_custo" : activeModule === "mensalidades_desconto" ? "mensalidades" : "geral"}
                  onOpenNewFinanceModal={() => setIsFinanceModalOpen(true)}
                  onOpenCostCenterModal={() => { setEditingCostCenter(null); setIsCostCenterModalOpen(true); }}
                  onEditCostCenter={handleEditCostCenter}
                  onOpenBankAccountModal={() => { setEditingBankAccount(null); setIsBankAccountModalOpen(true); }}
                  onEditBankAccount={handleEditBankAccount}
                  onDeleteBankAccount={handleDeleteBankAccount}
                  onOpenTransferModal={() => setIsTransferModalOpen(true)}
                  onClassifyTx={(tx) => setClassifyingTx(tx)}
                  onOpenImportStatement={() => setIsImportStatementOpen(true)}
                  onMarkPaid={handleMarkAsPaid}
                  onCancelPending={handleCancelPendingTx}
                  onEditPendingBank={(tx) => setEditingBankTx(tx)}
                  onUpdateTreasurySettings={handleUpdateTreasurySettings}
                  onDeleteCostCenter={handleDeleteCostCenter}
                  onDeleteTransaction={handleDeleteFinance}
                  onReverseTransaction={handleReverseFinance}
                  onCancelBatch={handleCancelBatch}
                  onViewMemberStatement={handleViewMemberStatement}
                  onViewReceipt={handleViewReceipt}
                  onMarkAsPaid={handleMarkAsPaid}
                />
              )}

              {/* Tesouraria / Clientes & Fornecedores */}
              {activeModule === "contatos" && (
                <ContactsView />
              )}

              {/* Chancelaria / Datas Comemorativas */}
              {activeModule === "datas_comemorativas" && (
                <CommemorativeDatesView />
              )}

              {/* Chancelaria / Presença por QR */}
              {activeModule === "presenca_qr" && (
                <AttendanceQRView sessions={sessions} />
              )}

              {/* Menu da Loja / Perfil da Loja */}
              {activeModule === "perfil_loja" && (
                <LodgeProfileView stats={stats} members={members} currentLoja={currentLoja} />
              )}

              {/* Menu da Loja / Meu Perfil & Extrato */}
              {activeModule === "meu_perfil" && (() => {
                // Matcher robusto: id -> email -> nome (normalizado)
                const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\bir\.?\s*/g, "").trim();
                const myMember = members.find(m => m.id === currentUser?.member_id)
                  || members.find(m => (m.email || "").toLowerCase() === (currentUser?.email || "").toLowerCase())
                  || members.find(m => currentUser?.name && norm(m.name).includes(norm(currentUser.name).split(" ")[0]) && norm(m.name).includes(norm(currentUser.name).split(" ").slice(-1)[0]));

                const handleEditMe = async () => {
                  if (!currentUser) {
                    toast.error("Aguardando autenticação. Tente novamente em instantes.");
                    return;
                  }
                  if (myMember) {
                    setEditingMember(myMember);
                    setIsMemberModalOpen(true);
                    return;
                  }
                  // Sem cadastro de Irmão vinculado: cria um a partir do currentUser
                  try {
                    const payload = {
                      name: currentUser.name?.replace(/\s*\((.*?)\)\s*/g, "").trim() || "Novo Irmão",
                      cim: currentUser.cim || `CIM-${Date.now().toString().slice(-6)}`,
                      degree: currentUser.degree || "3 - Mestre",
                      office: currentUser.office || "Membro do Quadro",
                      email: currentUser.email,
                      phone: "",
                      monthly_fee: 180.0,
                      status: "regular",
                    };
                    const res = await axios.post(`${API}/members`, payload);
                    toast.success("Cadastro de Irmão criado. Complete seus dados.");
                    await fetchData();
                    setEditingMember(res.data);
                    setIsMemberModalOpen(true);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Não foi possível criar seu cadastro.");
                  }
                };

                return (
                <div className="space-y-6">
                  <div className="luxury-card p-6 border-l-4 border-l-[#D4AF37] flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div>
                      <span className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold block">
                        Portal do Obreiro
                      </span>
                      <h2 className="text-2xl font-serif font-bold text-white mt-1">
                        {currentUser?.name}
                      </h2>
                      <p className="text-xs text-slate-400 font-mono mt-0.5">
                        CIM: {myMember?.cim || currentUser?.cim || "—"} • {myMember?.degree || currentUser?.degree || "—"} • Cargo: {myMember?.office || currentUser?.office || "Membro"}
                      </p>
                      {!myMember && (
                        <p className="text-[11px] text-amber-400 mt-2" data-testid="no-member-warning">
                          ⚠ Você ainda não tem um cadastro de Irmão. Clique em "Editar Meu Cadastro" para criar e completar seus dados agora.
                        </p>
                      )}
                    </div>

                    <div className="flex items-center space-x-3">
                      <button
                        onClick={handleEditMe}
                        data-testid="edit-my-profile-btn"
                        className="border border-[#D4AF37]/40 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 text-[#D4AF37] font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all"
                      >
                        {myMember ? "Editar Meu Cadastro" : "Criar Meu Cadastro"}
                      </button>
                      <button
                        onClick={() => handleViewMemberStatement(myMember?.id || currentUser?.member_id || members[0]?.id)}
                        disabled={!myMember && !currentUser?.member_id && members.length === 0}
                        className="bg-[#D4AF37] hover:bg-[#C5A028] disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider shadow-md transition-all"
                      >
                        Ver Meu Extrato Financeiro
                      </button>
                    </div>
                  </div>

                  <MembersDirectory
                    members={members}
                    lodgeInfo={{ name: currentLoja?.name, number: currentLoja?.number, oriente: currentLoja?.oriente }}
                    onOpenNewMemberModal={() => {}}
                    onOpenEditMemberModal={() => {}}
                    onDeleteMember={() => {}}
                    onViewMemberStatement={handleViewMemberStatement}
                    canManage={false}
                  />
                </div>
                );
              })()}

              {/* Menu da Loja / Agenda */}
              {activeModule === "agenda" && (
                <LodgeAgendaView
                  sessions={sessions}
                  onOpenNewSessionModal={() => setIsSessionModalOpen(true)}
                  canCreateSession={canScheduleSession}
                />
              )}

              {/* Tesouraria / Relatório por Centro de Custo */}
              {activeModule === "relatorio_cc" && (
                <CostCenterReport
                  costCenters={costCenters}
                  currentLoja={currentLoja}
                />
              )}

              {/* Menu da Loja / Mensagens & Avisos */}
              {activeModule === "mensagens" && (
                <LodgeMessagesView currentUser={currentUser} />
              )}

              {/* Menu da Loja / Arquivos (Nuvem) */}
              {activeModule === "arquivos" && (
                <LodgeFilesView />
              )}
            </>
          )}
        </main>

        {/* Footer */}
        <footer className="border-t border-slate-800/80 py-6 px-4 text-center text-xs text-slate-500">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
            <span data-testid="footer-loja-name">
              {currentLoja
                ? `Lojas Integradas • ${formatLojaName(currentLoja, { withArls: true })}${currentLoja.oriente ? ` (Or∴ ${currentLoja.oriente})` : ""}`
                : "Lojas Integradas • Plataforma SaaS Maçônica"}
            </span>
            <span className="font-mono text-[#D4AF37]">Liberdade • Igualdade • Fraternidade</span>
          </div>
        </footer>

      </div>

      {/* Modals */}
      <MemberModal
        isOpen={isMemberModalOpen}
        onClose={() => {
          setIsMemberModalOpen(false);
          setEditingMember(null);
        }}
        onSubmit={handleSaveMember}
        initialData={editingMember}
        isEditing={Boolean(editingMember)}
        defaultMonthlyFee={treasurySettings?.default_monthly_fee || 180}
      />

      <SessionModal
        isOpen={isSessionModalOpen}
        onClose={() => setIsSessionModalOpen(false)}
        onSubmit={handleSaveSession}
        members={members}
      />

      <GroupModal
        isOpen={isGroupModalOpen}
        onClose={() => {
          setIsGroupModalOpen(false);
          setEditingGroup(null);
        }}
        onSubmit={handleSaveGroup}
        initialData={editingGroup}
        isEditing={Boolean(editingGroup)}
      />

      <FinanceModal
        isOpen={isFinanceModalOpen}
        onClose={() => setIsFinanceModalOpen(false)}
        onSubmit={handleSaveFinance}
        members={members}
        contacts={contacts}
        costCenters={costCenters}
        bankAccounts={bankAccounts}
      />

      <CostCenterModal
        isOpen={isCostCenterModalOpen}
        onClose={() => { setIsCostCenterModalOpen(false); setEditingCostCenter(null); }}
        onSubmit={handleSaveCostCenter}
        initialData={editingCostCenter}
      />

      <BankAccountModal
        isOpen={isBankAccountModalOpen}
        onClose={() => { setIsBankAccountModalOpen(false); setEditingBankAccount(null); }}
        onSubmit={handleSaveBankAccount}
        initialData={editingBankAccount}
      />

      <TransferModal
        isOpen={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        onSubmit={handleTransfer}
        bankAccounts={bankAccounts}
      />

      <ClassifyModal
        isOpen={!!classifyingTx}
        onClose={() => setClassifyingTx(null)}
        onSubmit={handleClassify}
        transaction={classifyingTx}
        bankAccounts={bankAccounts}
        costCenters={costCenters}
      />

      <ImportStatementModal
        isOpen={isImportStatementOpen}
        onClose={() => setIsImportStatementOpen(false)}
        onSuccess={fetchData}
        bankAccounts={bankAccounts}
      />

      <ChangeBankModal
        isOpen={!!editingBankTx}
        onClose={() => setEditingBankTx(null)}
        transaction={editingBankTx}
        bankAccounts={bankAccounts}
        onSaved={fetchData}
      />

      <ChangePasswordModal
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />

      <MemberStatementModal
        isOpen={isStatementModalOpen}
        onClose={() => setIsStatementModalOpen(false)}
        statementData={selectedStatementData}
        treasurySettings={treasurySettings}
        costCenters={costCenters}
        onPayFee={handlePayFee}
        onMarkLinkedPaid={handleMarkAsPaid}
        onReload={async () => {
          if (selectedStatementData?.member?.id) {
            const updated = await axios.get(`${API}/members/${selectedStatementData.member.id}/statement`);
            setSelectedStatementData(updated.data);
          }
          fetchData();
        }}
        currentUserRole={currentUser?.role}
        canManage={canManageTreasury}
      />

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLogin={handleLogin}
        currentUser={currentUser}
      />

      <SessionQRModal
        isOpen={isQRModalOpen}
        onClose={() => setIsQRModalOpen(false)}
        session={selectedSessionForQR}
        members={members}
        onCheckedIn={fetchData}
      />

      <SendBoletimModal
        isOpen={isBoletimModalOpen}
        onClose={() => setIsBoletimModalOpen(false)}
        session={selectedSessionForBoletim}
        members={members}
        onSavedPending={() => setBoletimRefreshKey(k => k + 1)}
      />

      <ReceiptPrintModal
        isOpen={receiptModalOpen}
        onClose={() => setReceiptModalOpen(false)}
        receipt={activeReceipt}
        onSendEmail={handleSendReceiptEmail}
      />

      </div>
    </div>
  );
}

